import React, { useEffect, useState, useCallback,ChangeEvent } from 'react';
import deepEqual from 'deep-equal';
import { useMutation } from 'react-query'
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';

import Area, { AreaData } from '../model/Area';
import { createArea, updateArea } from '../database';


interface NewAreaProps {
  show: boolean;
  onHide: () => void;
  parent: string;
}

interface EditAreaProps {
  show: boolean;
  onHide: () => void;
  area: Area;
}

type AreaModalProps = NewAreaProps | EditAreaProps;

function isNew(obj: {parent:string}|{area:Area}): obj is {parent:string} {
  try {
    return (obj as {parent:string}).parent !== undefined;
  } catch {
    return false;
  }
}

export default function AreaModal({show, onHide, ...props }: AreaModalProps) {
  const [oldProps, setOldProps] = useState(props);
  const [oldArea, setOldArea] = useState(isNew(props) ? undefined : props.area);
  const [name, setName] = useState(isNew(props) ? '' : props.area.name);
  const [description, setDescription] = useState(isNew(props) ? '' : props.area.description);
  const [directions, setDirections] = useState(isNew(props) ? '' : props.area.directions)
  const [isPrivate, setIsPrivate] = useState(isNew(props) ? false : props.area.private);
  const [order, setOrder] = useState(isNew(props) ? null : props.area.order);
  const [parent, setParent] = useState(isNew(props) ? props.parent : props.area.parent)
  // TODO: Create a react-like rock type form control
  const [rockTypes] = useState(isNew(props) ? [] : props.area.rockType);

  // useState won't update bindings that rely on `isNew(props)` for some
  // reason, do it manaully here, also its firing even though props isn't
  // changing, what the fuck?
  useEffect(() => {
    if (deepEqual(oldProps, props)) {
      return;
    }
    setOldProps(props);

    if (isNew(props)) {
      setOldArea(undefined);
      setName('');
      setDescription('');
      setDirections('');
      setIsPrivate(false);
      setOrder(null);
      setParent(props.parent)
    } else {
      setOldArea(props.area);
      setName(props.area.name);
      setDescription(props.area.description);
      setDirections(props.area.directions);
      setIsPrivate(props.area.private);
      setOrder(props.area.order);
      setParent(props.area.parent)
    }
  }, [oldProps, props])

  const { mutateAsync: mutateArea } = useMutation((): Promise<void> => {
    const newArea: AreaData = {
      name: name,
      description: description,
      directions: directions,
      private: isPrivate,
      order: order,
      parent: parent,
      rockType: rockTypes
    }
    if (oldArea) {
      // TODO: It'd be nice to use a diff to generate UpdateData<AreaData>
      //       with field values if we ever need to delete a field.
      return updateArea(oldArea.id, newArea);
    } else {
      return createArea(newArea);
    }
  });

  const handleSave = useCallback(async () => {
    try {
      await mutateArea();
    } catch (e) {
      console.log('There was a problem mutating area:', e);
    }
    onHide();
  }, [onHide, mutateArea]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>{oldArea ? 'Update Area' : 'Create Area'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control
              defaultValue={name}
              onChange={e => setName(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              defaultValue={description}
              onChange={e => setDescription(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Directions</Form.Label>
            <Form.Control
              as="textarea"
              defaultValue={directions}
              onChange={e => setDirections(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Privateity</Form.Label>
            <Form.Check
              id="meaningless-id-to-make-this-work"
              type="switch"
              defaultChecked={isPrivate}
              label="Private"
              onChange={(e: ChangeEvent<HTMLInputElement>) => setIsPrivate(e.target.checked)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
        <Button variant="primary" onClick={handleSave}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
}

